import React from "react";
import {
  Briefcase,
  Target,
  Users,
  TrendingUp,
  Calendar,
  Award,
  MessageSquare,
  CheckCircle2,
  XCircle,
  FileText,
} from "lucide-react";

const PromoterProfileDetails = ({ promoter }) => {
  if (!promoter) return null;

  const formatArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return "Not specified";
    return arr.join(", ");
  };

  const formatPostingFrequency = (freq) => {
    if (!freq) return "Not specified";
    return freq.charAt(0).toUpperCase() + freq.slice(1);
  };

  return (
    <div className="p-6 bg-white rounded-xl border border-gray-200">
      <h3 className="flex gap-2 items-center mb-4 text-lg font-semibold text-gray-900">
        <FileText className="w-5 h-5 text-[#FF6C6C]" />
        Promoter Profile Details
      </h3>

      <div className="space-y-4">
        {/* Account Type & ID */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="mb-3 text-sm font-semibold text-gray-900">Account Information</h4>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium text-gray-600">Account Type</p>
              <p className="text-sm font-semibold text-gray-900 capitalize">
                {promoter.account_type || "Not specified"}
              </p>
            </div>
            {promoter.id_type && (
              <>
                <div>
                  <p className="text-xs font-medium text-gray-600">ID Type</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {promoter.id_type.replace(/_/g, " ").toUpperCase()}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs font-medium text-gray-600">ID Number</p>
                  <p className="text-sm font-semibold text-gray-900">{promoter.id_number || "Not provided"}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Categories */}
        {promoter.content_categories && (
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex gap-2 items-center mb-2">
              <Target className="w-4 h-4 text-purple-600" />
              <h4 className="text-sm font-semibold text-purple-900">Content Categories</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(promoter.content_categories) &&
                promoter.content_categories.map((category, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded"
                  >
                    {category}
                  </span>
                ))}
              {(!Array.isArray(promoter.content_categories) || promoter.content_categories.length === 0) && (
                <span className="text-sm text-purple-700">Not specified</span>
              )}
            </div>
          </div>
        )}

        {/* Target Audiences */}
        {promoter.target_audiences && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex gap-2 items-center mb-2">
              <Users className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-semibold text-blue-900">Target Audiences</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(promoter.target_audiences) &&
                promoter.target_audiences.map((audience, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded"
                  >
                    {audience}
                  </span>
                ))}
              {(!Array.isArray(promoter.target_audiences) || promoter.target_audiences.length === 0) && (
                <span className="text-sm text-blue-700">Not specified</span>
              )}
            </div>
          </div>
        )}

        {/* Posting Activity */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex gap-2 items-center mb-3">
            <Calendar className="w-4 h-4 text-green-600" />
            <h4 className="text-sm font-semibold text-green-900">Posting Activity</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-green-700">Frequency</p>
              <p className="text-sm font-semibold text-green-900">
                {formatPostingFrequency(promoter.posting_frequency)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-green-700">Posts Per Week</p>
              <p className="text-sm font-semibold text-green-900">
                {promoter.weekly_post_count || "Not specified"}
              </p>
            </div>
          </div>
        </div>

        {/* Affiliate Experience */}
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <div className="flex gap-2 items-center mb-3">
            <Award className="w-4 h-4 text-orange-600" />
            <h4 className="text-sm font-semibold text-orange-900">Affiliate Experience</h4>
          </div>
          <div className="space-y-2">
            <div className="flex gap-2 items-center">
              <span className="text-xs font-medium text-orange-700">Has Experience:</span>
              {promoter.has_affiliate_experience ? (
                <span className="flex gap-1 items-center text-sm font-semibold text-green-700">
                  <CheckCircle2 className="w-4 h-4" /> Yes
                </span>
              ) : (
                <span className="flex gap-1 items-center text-sm font-semibold text-gray-600">
                  <XCircle className="w-4 h-4" /> No
                </span>
              )}
            </div>
            {promoter.previous_affiliate_platforms && (
              <div>
                <p className="text-xs font-medium text-orange-700">Previous Platforms:</p>
                <p className="text-sm text-orange-900">{promoter.previous_affiliate_platforms}</p>
              </div>
            )}
          </div>
        </div>

        {/* Motivation */}
        {promoter.motivation_to_join && (
          <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex gap-2 items-center mb-2">
              <MessageSquare className="w-4 h-4 text-indigo-600" />
              <h4 className="text-sm font-semibold text-indigo-900">Motivation to Join</h4>
            </div>
            <p className="text-sm text-indigo-900">{promoter.motivation_to_join}</p>
          </div>
        )}

        {/* Compliance Agreements */}
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="mb-3 text-sm font-semibold text-gray-900">Compliance Agreements</h4>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="flex gap-2 items-center text-xs">
              {promoter.agree_performance_based ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className={promoter.agree_performance_based ? "text-gray-900" : "text-gray-500"}>
                Performance-based earnings
              </span>
            </div>
            <div className="flex gap-2 items-center text-xs">
              {promoter.agree_no_spam ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className={promoter.agree_no_spam ? "text-gray-900" : "text-gray-500"}>
                No spam policy
              </span>
            </div>
            <div className="flex gap-2 items-center text-xs">
              {promoter.agree_follow_rules ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className={promoter.agree_follow_rules ? "text-gray-900" : "text-gray-500"}>
                Follow platform rules
              </span>
            </div>
            <div className="flex gap-2 items-center text-xs">
              {promoter.agree_accept_consequences ? (
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className={promoter.agree_accept_consequences ? "text-gray-900" : "text-gray-500"}>
                Accept consequences
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoterProfileDetails;
